import {
  CHARACTER_INDICES,
  REG_EXPS,
  SPECIAL_CHARACTERS,
} from "@plugins/richtext/plugins/character-word-count/utils/character.word.count.constants";
import { isEmpty } from "@utils/var.check";

const strLen = (str) => {
  return str.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "_").length;
};

const countCharacters = (text) => {
  // new lines are not considered
  if (!isEmpty(text)) {
    return strLen(text.replace(/\n/g, ""));
  }
  return 0;
};
//Remove zero-width space
const removeZwsp = (text) => {
  return text.replace(/\u200B/g, "");
};
//Set default options to include space and punctuation for word count
const getDefaultOptions = () => {
  return {
    includeWhitespace: false,
    includePunctuation: true,
  };
};
//Generate Sets of regular expression
const getRegExpSets = () => {
  const regExpSet = new Set();
  for (const key in REG_EXPS) {
    regExpSet.add(new RegExp(REG_EXPS[key]));
  }
  return regExpSet;
};
//map each char using the memoized version of getType and return the type(character_indices)
const mapChars = (chars, func) => {
  const length = chars.length;
  let charArray = new Array(length);
  for (let i = 0; i < length; i++) {
    const ch = chars[i];
    charArray[i] = func(ch, i);
  }
  return charArray;
};
//Determine type of each char by testing against the regular expressions created from REG_EXPS
const getType = function (char) {
  let type = CHARACTER_INDICES.OTHER;
  const regExpSets = getRegExpSets();
  for (let i = 0; i < regExpSets.length; ++i) {
    const set = regExpSets[i];
    if (set && set.test(char)) {
      type = i;
      break;
    }
  }
  return type;
};
//caching the result of getType and return the cached results when same input happens again
const memoize = (func) => {
  const cache = {};
  return function (char) {
    if (cache[char]) {
      return cache[char];
    } else {
      const result = func(char);
      cache[char] = result;
      return result;
    }
  };
};
//Classify each character into their respective character indices
const classifyCharacters = (characters) => {
  let memoized = memoize(getType);
  return mapChars(characters, memoized);
};
//Checks if the character at the given index is a boundary or not
const isWordBoundary = (map, index) => {
  const type = map[index];
  const nextType = map[index + 1];

  if (index < 0 || (index > map.length - 1 && index !== 0)) {
    return false;
  }

  if (type === CHARACTER_INDICES.ALETTER && nextType === CHARACTER_INDICES.ALETTER) {
    return false;
  }

  const nextNextType = map[index + 2];

  if (
    type === CHARACTER_INDICES.ALETTER &&
    (nextType === CHARACTER_INDICES.MIDLETTER ||
      nextType === CHARACTER_INDICES.MIDNUMLET ||
      nextType === CHARACTER_INDICES.AT) &&
    nextNextType === CHARACTER_INDICES.ALETTER
  ) {
    return false;
  }

  const prevType = map[index - 1];

  if (
    (type === CHARACTER_INDICES.MIDLETTER ||
      type === CHARACTER_INDICES.MIDNUMLET ||
      nextType === CHARACTER_INDICES.AT) &&
    nextType === CHARACTER_INDICES.ALETTER &&
    prevType === CHARACTER_INDICES.ALETTER
  ) {
    return false;
  }

  if (
    (type === CHARACTER_INDICES.NUMERIC || type === CHARACTER_INDICES.ALETTER) &&
    (nextType === CHARACTER_INDICES.NUMERIC || nextType === CHARACTER_INDICES.ALETTER)
  ) {
    return false;
  }

  if (
    (type === CHARACTER_INDICES.MIDNUM || type === CHARACTER_INDICES.MIDNUMLET) &&
    nextType === CHARACTER_INDICES.NUMERIC &&
    prevType === CHARACTER_INDICES.NUMERIC
  ) {
    return false;
  }

  if (
    type === CHARACTER_INDICES.NUMERIC &&
    (nextType === CHARACTER_INDICES.MIDNUM || nextType === CHARACTER_INDICES.MIDNUMLET) &&
    nextNextType === CHARACTER_INDICES.NUMERIC
  ) {
    return false;
  }

  if (
    type === CHARACTER_INDICES.EXTEND ||
    type === CHARACTER_INDICES.FORMAT ||
    prevType === CHARACTER_INDICES.EXTEND ||
    prevType === CHARACTER_INDICES.FORMAT ||
    nextType === CHARACTER_INDICES.EXTEND ||
    nextType === CHARACTER_INDICES.FORMAT
  ) {
    return false;
  }

  if (type === CHARACTER_INDICES.CR && nextType === CHARACTER_INDICES.LF) {
    return false;
  }

  if (
    type === CHARACTER_INDICES.NEWLINE ||
    type === CHARACTER_INDICES.CR ||
    type === CHARACTER_INDICES.LF
  ) {
    return true;
  }

  if (
    nextType === CHARACTER_INDICES.NEWLINE ||
    nextType === CHARACTER_INDICES.CR ||
    nextType === CHARACTER_INDICES.LF
  ) {
    return true;
  }

  if (type === CHARACTER_INDICES.KATAKANA && nextType === CHARACTER_INDICES.KATAKANA) {
    return false;
  }

  if (
    nextType === CHARACTER_INDICES.EXTENDNUMLET &&
    (type === CHARACTER_INDICES.ALETTER ||
      type === CHARACTER_INDICES.NUMERIC ||
      type === CHARACTER_INDICES.KATAKANA ||
      type === CHARACTER_INDICES.EXTENDNUMLET)
  ) {
    return false;
  }

  if (
    type === CHARACTER_INDICES.EXTENDNUMLET &&
    (nextType === CHARACTER_INDICES.ALETTER ||
      nextType === CHARACTER_INDICES.NUMERIC ||
      nextType === CHARACTER_INDICES.KATAKANA)
  ) {
    return false;
  }

  return type !== CHARACTER_INDICES.AT;
};

const isProtocol = (str) => {
  return str === "http" || str === "https";
};
//find the end of a word(if hits white space) using the start index
const findWordEnd = (characters, startIndex) => {
  let i;

  for (i = startIndex; i < characters.length; i++) {
    if (SPECIAL_CHARACTERS.WHITESPACE.test(characters[i])) {
      break;
    }
  }

  return i;
};

const findUrlEnd = (characters, startIndex) => {
  const endIndex = findWordEnd(characters, startIndex + 1);
  const peakedWord = characters
    .slice(startIndex + 1, endIndex)
    .join(SPECIAL_CHARACTERS.EMPTY_STRING);
  return peakedWord.substr(0, 3) === "://" ? endIndex : startIndex;
};

//Find words by iterating over the characterMap(char:indices) and check word boundary
const findWords = (chars, extractedChars, characterMap, options) => {
  let words = [];
  let word = [];

  for (let i = 0; i < characterMap.length; ++i) {
    word.push(chars[i]);
    if (isWordBoundary(characterMap, i)) {
      const ch = extractedChars[i];
      if (
        (options.includeWhitespace || !SPECIAL_CHARACTERS.WHITESPACE.test(ch)) &&
        (options.includePunctuation || !SPECIAL_CHARACTERS.PUNCTUATION.test(ch))
      ) {
        const startOfWord = i - word.length + 1;
        const endOfWord = i + 1;
        const str = extractedChars
          .slice(startOfWord, endOfWord)
          .join(SPECIAL_CHARACTERS.EMPTY_STRING);

        if (isProtocol(str)) {
          const endOfUrl = findUrlEnd(extractedChars, i);
          const url = chars.slice(endOfWord, endOfUrl);
          Array.prototype.push.apply(word, url);
          i = endOfUrl;
        }
        words.push(word);
      }
      word = [];
    }
  }
  return words;
};

const getWords = (text) => {
  let defaultOptions = Object.assign({}, getDefaultOptions());
  let filteredChars = [];
  let extractedChars = [];
  text.forEach((char) => {
    const ch = char;
    if (ch !== SPECIAL_CHARACTERS.ZERO_WIDTH) {
      filteredChars.push(char);
      extractedChars.push(ch);
    }
  });
  let characterMap = classifyCharacters(extractedChars);
  return findWords(filteredChars, extractedChars, characterMap, defaultOptions);
};

const countWords = (text) => {
  let processedText = removeZwsp(text).replace(/\n/g, " ");
  //To remove trailing spaces and check for double spacing
  let trimmedText = processedText
    .trim()
    .split(/[\s\t\n]+/)
    .join(" ");
  if (!isEmpty(trimmedText)) {
    return getWords(trimmedText.split(" ")).length;
  }
  return 0;
};

export {
  strLen,
  countCharacters,
  removeZwsp,
  getDefaultOptions,
  getRegExpSets,
  mapChars,
  getType,
  memoize,
  classifyCharacters,
  isWordBoundary,
  isProtocol,
  findWordEnd,
  findUrlEnd,
  findWords,
  getWords,
  countWords,
};
